// Display formatting helpers.
export const formatNumber = (n) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString();

// The business runs on Israel time, so any timestamp/date shown in the app is
// always rendered in Israel time — regardless of the viewer's own browser
// timezone. Without this, a date-only value (e.g. "2026-08-05") can even shift
// to the wrong calendar day for a viewer in a timezone behind UTC.
const ISRAEL_TZ = 'Asia/Jerusalem';

// A timestamp with time-of-day, e.g. "6 Aug, 14:58".
export const formatIsraelTime = (s) => (s
  ? new Date(s).toLocaleString('en-GB', { timeZone: ISRAEL_TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—');

// A date-only value, e.g. "6 Aug 2026".
export const formatIsraelDate = (s) => (s
  ? new Date(s).toLocaleDateString('en-GB', { timeZone: ISRAEL_TZ, day: 'numeric', month: 'short', year: 'numeric' })
  : '—');

// Israel-time calendar day as "YYYY-MM-DD", for comparing two instants by day.
const israelDayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: ISRAEL_TZ });

// A timestamp split for a two-line table cell: a human day label on top and the
// time underneath. The day reads "Today" / "Yesterday" for the recent rows an
// operator actually cares about, falls back to "24 Aug", and only carries the
// year once the row is from a different year — so the column stays scannable.
export const formatIsraelDayTime = (s) => {
  if (!s) return { day: '\u2014', time: '' };
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return { day: '\u2014', time: '' };

  const now = new Date();
  const yesterday = new Date(now.getTime() - 86_400_000);
  const key = israelDayKey(d);

  let day;
  if (key === israelDayKey(now)) day = 'Today';
  else if (key === israelDayKey(yesterday)) day = 'Yesterday';
  else {
    const sameYear = key.slice(0, 4) === israelDayKey(now).slice(0, 4);
    day = d.toLocaleDateString('en-GB', {
      timeZone: ISRAEL_TZ, day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }),
    });
  }

  const time = d.toLocaleTimeString('en-GB', {
    timeZone: ISRAEL_TZ, hour: '2-digit', minute: '2-digit',
  });
  return { day, time };
};

export const formatPercent = (n) =>
  n === null || n === undefined ? '—' : `${Number(n).toFixed(1)}%`;

// Compact axis-tick style: 2.5k, 135k, 1.2M. Full numbers still show in tooltips/tables.
export const formatCompact = (n) => {
  if (n === null || n === undefined) return '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${+(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return String(n);
};
